<template>
  <div
    v-if="!hideTile"
    :class="{ 'overview-container--loading': loading }"
    class="overview-container"
  >
    <!-- SHOW ERROR -->
    <span v-if="!loading && showErrorTile" style="display: contents">
      <ErrorTile :title="$t('errorTitle')"></ErrorTile>
    </span>

    <!-- SHOW TILE -->
    <span v-if="!showErrorTile">
      <Card v-if="loading || !resolvedPolicy" :loadingState="true"></Card>
      <Card
        v-if="resolvedPolicy && !loading"
        :id="'policy.clickable-policy-card.' + policy.policyId"
        :iconClass="motorIconClass"
        :isPolicy="true"
        :isPolicyDetails="isDetailHeader"
        :title="motorTitle"
      >
        <template v-if="!isEMobilContract(motorContract)" #title-extra>
          <div class="license-number">
            {{ licenseNumber }}
            <template v-if="isSeasonal">/ {{ $t('motorPolicyTile.seasonal') }}</template>
          </div>
        </template>

        <!-- CONTENT-->
        <template #content>
          <GridContent :with-padding="false">
            <template #gridContent>
              <dl class="info">
                <dt class="info__label">{{ $t('motorPolicyTile.policyNumber') }}</dt>
                <dd class="info__value">{{ resolvedPolicy.policyNumber }}</dd>
              </dl>
              <dl>
                <dt class="info__label">{{ $t('motorPolicyTile.statusLabel') }}</dt>
                <dd class="info__value">
                  <span :class="statusClassesForPolicy(resolvedPolicy as Policy)"></span
                  >{{
                    resolvedPolicy.policyStatus
                      ? $t('motorPolicyTile.policyStatus.' + resolvedPolicy.policyStatus)
                      : ''
                  }}
                </dd>
              </dl>
              <dl v-if="isDetailHeader">
                <dt class="info__label">
                  {{ $t('motorPolicyTile.beginningDatePolicy') }}
                </dt>
                <dd class="info__value">
                  {{ formatDate(resolvedPolicy!.beginningDatePolicy!) }}
                </dd>
              </dl>
              <dl v-if="showGeneralTermsDate && isDetailHeader">
                <dt class="info__label">
                  {{ $t('motorPolicyTile.modificationDatePolicy') }}
                </dt>
                <dd class="info__value">
                  {{ formatDate(resolvedPolicy!.modificationDatePolicy!) }}
                </dd>
              </dl>
              <dl v-if="showEMopedValues && isDetailHeader">
                <dt class="info__label">
                  {{ $t('motorPolicyTile.expiryDatePolicy') }}
                </dt>
                <dd class="info__value">
                  {{ formatDate(resolvedPolicy!.expiryDatePolicy!) }}
                </dd>
              </dl>
            </template>
          </GridContent>
        </template>

        <!-- FOOTER-->
        <template v-if="isDetailHeader" #footer>
          <GridContent :with-padding="false">
            <template #gridContent>
              <a
                v-for="link in filterHiddenServiceLinks(specificServiceLinks)"
                :id="'policy-' + link.name + link.href"
                ref="serviceLinks"
                class="service-link"
                tabindex="0"
                :key="link.href"
                :data-track-id="link.name"
                @click.prevent="goToLink(sanitizeUrl(link.href), link.name)"
                @keydown.enter.prevent="goToLink(sanitizeUrl(link.href), link.name)"
                @keydown.space.prevent="goToLink(sanitizeUrl(link.href), link.name)"
              >
                {{ $t('motorPolicyTile.serviceLink.' + link.name) }}
              </a>
            </template>
          </GridContent>
        </template>

        <template v-else-if="!hasRole('helpdesk')" #footer>
          <div class="footer-cta footer-cta--left">
            <CustomButton
              :id="'policy.details-button.' + resolvedPolicy.policyNumber"
              @click="goToDetailView(policy)"
              :buttonColor="ButtonColor.SECONDARY"
            >
              {{ $t('motorPolicyTile.buttonText') }}
            </CustomButton>
          </div>
        </template>
      </Card>
    </span>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, type PropType, type Ref, ref } from 'vue'
import {
  ButtonColor,
  Card,
  CustomButton,
  GridContent,
  type PolicyWithIconAndTitles
} from 'de-zurich-ui-library'
import ErrorTile from '@/components/common/ErrorTile.vue'
import {
  formatDate,
  goToDetailView,
  mapContractBase,
  statusClassesForPolicy
} from '@/utils/policy/PolicyUtils'
import type { Policy } from 'policies-api'
import type { ServiceLink, ServiceLinkContext } from '@/types/policy/PolicyTypes'
import { navigate } from '@/utils/NavigationUtils'
import {
  filterHiddenServiceLinks,
  getCookie,
  resolveServiceLinksFromRules
} from '@/utils/utils'
import { useMotorStore } from '@/stores/motorStore'
import { sanitizeUrl } from '@braintree/sanitize-url'
import { type EMobilContract, isEMobilContract } from '@/types/emobil/EMobilContract'
import { type MotorContract } from '@/types/motor/MotorContract'
import { hasRole } from '@/utils/KeycloakUtils'
import {
  isEMoped,
  resolveMotorIconString,
  resolveMotorTitle,
  swapLettersAndNumbers
} from '@/utils/motor/MotorUtilsCommon'
import { getServiceLinkRules } from '@/utilsTenant/serviceLinkFilterRules'

const props = defineProps({
  isDetailHeader: {
    type: Boolean,
    default: false
  },
  policy: {
    type: Object as PropType<Policy>,
    required: true
  }
})

const vehicleChangeUrl = globalThis.ENV_CONFIG?.REDIRECTS?.KFZ_VEHICLE_CHANGE

const motorContract: Ref<MotorContract | EMobilContract | null> = ref(null)
const loading = ref(true)
const hideTile = ref(false)
const isSeasonal = ref(false)
const resolvedPolicy: Ref<Policy | PolicyWithIconAndTitles | null> = ref(null)
const showErrorTile = ref(false)
const specificServiceLinks: Ref<ServiceLink[]> = ref([])

const licenseNumber = computed(() => {
  const licenseNumberValue =
    !isEMobilContract(motorContract.value) &&
    (motorContract.value?.insuredVehicle?.licenceNumber ||
      resolvedPolicy.value?.policyLabel ||
      '')

  if (licenseNumberValue) {
    const regex = /^[A-Z]{3}\d{3}$/
    // Test if it is a eMobil License Number
    const isEMobilLicenseNumber = regex.test(licenseNumberValue)

    // when it is a eMobil License Number, swap letters and numbers
    // and return the string
    if (isEMobilLicenseNumber) return swapLettersAndNumbers(licenseNumberValue)
  }

  // if it does not match eMobil or string does not exist return original value
  return licenseNumberValue
})

const motorIconClass = computed(() => {
  return resolveMotorIconString(motorContract.value ?? undefined, props.policy)
})

const motorTitle = computed(() => {
  return resolveMotorTitle(motorContract.value ?? undefined, props.policy)
})

const showGeneralTermsDate = computed(() => {
  return (
    globalThis.ENV_CONFIG?.FEATURE_FLAGS.MOTOR &&
    !!resolvedPolicy.value?.modificationDatePolicy
  )
})

const showEMopedValues = computed(() => {
  // darf kein Emobil-Contract sein muss aber ein passendes wkz haben für Emoped
  return (
    !isEMobilContract(motorContract.value) &&
    isEMoped(motorContract.value?.vehicleType.code as string)
  )
})

const isVehicleChangeEnabled = computed(() => {
  // Red cooperation partners are not allowed to make vehicle changes
  return (
    globalThis.ENV_CONFIG?.FEATURE_FLAGS?.MOTOR?.ENABLE_MOTOR_CHANGE === true &&
    !isRedCooperationPartner()
  )
})

onMounted(async () => {
  const t_policyId = props.policy.policyId

  await useMotorStore()
    .getMotorContractByPolicyId(t_policyId)
    .then((motorContractResp) => {
      if (!motorContractResp) {
        loading.value = false
        hideTile.value = true
        return
      }
      motorContract.value = motorContractResp
      resolvedPolicy.value = {} as Policy
      resolvedPolicy.value.partnerAddressIDVN = props.policy.partnerAddressIDVN
      mapContractBase(motorContract.value.contractBase, resolvedPolicy.value)
    })
    .catch(() => {
      showErrorTile.value = true
    })
    .finally(() => {
      loading.value = false
      isSeasonal.value = isEMobilContract(motorContract.value)
        ? false
        : !!motorContract.value?.insuredVehicle?.seasonalPeriod

      if (motorContract.value) {
        // Regelbasierte Auflösung der Service-Links über tenant-spezifische Filterregeln.
        // Die Regeln sind in @/utilsTenant/serviceLinkFilterRules definiert.
        const context: ServiceLinkContext = {
          ddsLines: props.policy.ddsLines ?? [],
          isRedCoop: isRedCooperationPartner(),
          policyStatus: resolvedPolicy.value?.policyStatus ?? '',
          // eMobil-Verträge haben kein WKZ → leerer String, damit kein WKZ-Präfix passt.
          // SHUR/Life verwenden undefined → WKZ-Check wird in resolveServiceLinksFromRules übersprungen.
          wkz: isEMobilContract(motorContract.value)
            ? ''
            : (motorContract.value as MotorContract)?.vehicleType?.code,
          expiryDatePolicy: resolvedPolicy.value?.expiryDatePolicy ?? undefined
        }
        specificServiceLinks.value = resolveServiceLinksFromRules(
          getServiceLinkRules(),
          context
        )
      }
    })
})

function isRedCooperationPartner(): boolean {
  const redCookie = getCookie('isRedCooperationPartner')
  if (!redCookie) {
    // if cookie is unset, we assume red cooperation for security reasons
    return true
  }
  return redCookie !== 'false'
}

function goToVehicleChangeView(): void {
  if (!resolvedPolicy.value || !props.policy.policyId) {
    console.error('PolicyId missing', resolvedPolicy.value)
    return
  }
  if (vehicleChangeUrl) {
    let finalVehicleChangeUrl: string = vehicleChangeUrl.replaceAll(
      '<POLICY_ID>',
      props.policy.policyId
    )
    if (resolvedPolicy.value.partnerAddressIDVN) {
      finalVehicleChangeUrl = finalVehicleChangeUrl
        .replaceAll(
          '<PARTNER_ADDRESS_ID>',
          resolvedPolicy.value.partnerAddressIDVN.toString()
        )
        .slice(1)
    }
    navigate(finalVehicleChangeUrl)
  }
}

function goToLink(url: string, name: string): void {
  if (url == 'changeVehicle' && isVehicleChangeEnabled) {
    goToVehicleChangeView()
    return
  }

  if (!resolvedPolicy.value || !props.policy.policyId) {
    console.error('PolicyId missing', resolvedPolicy.value)
    return
  }
  if (url) {
    const newPath =
      '/vertraege/policen/motor/<PARTNER_ADDRESS_ID>/<POLICY_ID>/vertrag-kuendigen'
        .replaceAll('<POLICY_ID>', props.policy.policyId)
        .replaceAll(
          '<PARTNER_ADDRESS_ID>',
          resolvedPolicy.value.partnerAddressIDVN?.toString() ?? ''
        )
    const targetUrl = url
    if (name === 'contractTermination') {
      navigate(newPath)
      return
    }
    navigate(targetUrl)
  }
}
</script>
